import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCrosshairs, faPlay, faPause, faStop, faSpinner,
  faPlus, faTrash, faMapMarkerAlt, faBolt, faSlidersH, faTable
} from '@fortawesome/free-solid-svg-icons';
import intruderService from '../../services/intruderServiceFrontend';
import authProfileService from '../../services/authProfileService';
import ProfileSelect from './ProfileSelect';
import IntruderPositions from './IntruderPositions';
import IntruderPayloads from './IntruderPayloads';
import IntruderProcessing from './IntruderProcessing';
import IntruderOptions from './IntruderOptions';
import IntruderResults from './IntruderResults';

const INNER_TABS = [
  { id: 'positions', label: 'Positions', icon: faMapMarkerAlt },
  { id: 'payloads', label: 'Payloads', icon: faBolt },
  { id: 'options', label: 'Options', icon: faSlidersH },
  { id: 'results', label: 'Results', icon: faTable },
];

const IntruderTab = ({ webTarget, initialExchange, isDarkMode }) => {
  const [attacks, setAttacks] = useState([]);
  const [selectedAttackId, setSelectedAttackId] = useState(null);
  const [innerTab, setInnerTab] = useState('positions');
  const [creating, setCreating] = useState(false);
  const [resultDetail, setResultDetail] = useState(null);
  const statusPollRef = useRef(null);
  const saveTimerRef = useRef(null);
  const initialExchangeProcessed = useRef(null);

  // Attack form state
  const [attackName, setAttackName] = useState('');
  const [attackMode, setAttackMode] = useState('sniper');
  const [method, setMethod] = useState('GET');
  const [url, setUrl] = useState('');
  const [headers, setHeaders] = useState([{ name: 'User-Agent', value: 'Zerberos/1.0' }]);
  const [body, setBody] = useState('');
  const [authProfile, setAuthProfile] = useState('');
  const [authProfiles, setAuthProfiles] = useState([]);
  const [positions, setPositions] = useState([]);
  const [payloadSets, setPayloadSets] = useState([{
    type: 'simple_list', values: [], numberConfig: { from: 0, to: 100, step: 1, format: 'decimal', minDigits: 1 }, nullCount: 100, processing: []
  }]);
  const [processing, setProcessing] = useState([]);
  const [grepMatch, setGrepMatch] = useState([]);
  const [grepExtract, setGrepExtract] = useState([]);
  const [throttle, setThrottle] = useState({ requestsPerSecond: 10, maxConcurrent: 1, delayBetweenRequests: 100 });

  // Tracks whether the current form has a DB entry
  const [editingAttackId, setEditingAttackId] = useState(null);

  // --- Load attacks on mount ---
  useEffect(() => {
    if (webTarget?._id) loadAttacks();
  }, [webTarget?._id]);

  // --- Load auth profiles for the picker ---
  useEffect(() => {
    if (webTarget?.project) {
      authProfileService.getAll(webTarget.project).then(setAuthProfiles).catch(() => {});
    }
  }, [webTarget?.project]);

  // --- "Send to Intruder" — auto-create a draft attack ---
  useEffect(() => {
    if (!initialExchange?.request || !webTarget?._id) return;
    const exchangeKey = initialExchange._id || `${initialExchange.request.method}:${initialExchange.request.url}`;
    if (initialExchangeProcessed.current === exchangeKey) return;
    initialExchangeProcessed.current = exchangeKey;

    const createDraft = async () => {
      try {
        let name = 'New Attack';
        try {
          const parsed = new URL(initialExchange.request.url);
          const pathParts = parsed.pathname.split('/').filter(Boolean);
          name = pathParts[pathParts.length - 1] || parsed.hostname;
          if (name.length > 30) name = name.slice(0, 30);
        } catch {}

        const attack = await intruderService.create({
          project: webTarget.project,
          webTarget: webTarget._id,
          name,
          attackMode: 'sniper',
          baseRequest: {
            method: initialExchange.request.method || 'GET',
            url: initialExchange.request.url || '',
            headers: initialExchange.request.headers || [{ name: 'User-Agent', value: 'Zerberos/1.0' }],
            body: initialExchange.request.body || '',
          },
          insertionPoints: [],
          payloadSets: [{ insertionPointId: 'ip1', type: 'simple_list', values: [], processing: [] }],
          throttle: { requestsPerSecond: 10, maxConcurrent: 1, delayBetweenRequests: 100 },
        });

        await loadAttacks();
        selectAttack(attack);
      } catch (err) {
        console.error('Error creating draft attack:', err);
      }
    };
    createDraft();
  }, [initialExchange, webTarget?._id]);

  // --- Poll for running attack status ---
  useEffect(() => {
    const running = attacks.filter(a => a.status === 'running');
    if (running.length > 0) {
      statusPollRef.current = setInterval(async () => {
        try {
          const data = await intruderService.getAll({ webTargetId: webTarget._id });
          setAttacks(data);
        } catch {}
      }, 3000);
    }
    return () => {
      if (statusPollRef.current) {
        clearInterval(statusPollRef.current);
        statusPollRef.current = null;
      }
    };
  }, [attacks.filter(a => a.status === 'running').length, webTarget?._id]);

  // --- Auto-save form to DB (debounced) ---
  const saveToDb = useCallback(async () => {
    if (!editingAttackId) return;
    try {
      await intruderService.update(editingAttackId, buildPayload());
      // Silently refresh attack list for sidebar
      const data = await intruderService.getAll({ webTargetId: webTarget._id });
      setAttacks(data);
    } catch {}
  }, [editingAttackId, attackName, attackMode, authProfile, method, url, headers, body, positions, payloadSets, processing, grepMatch, grepExtract, throttle]);

  useEffect(() => {
    if (!editingAttackId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(saveToDb, 2000);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [attackName, attackMode, method, url, headers, body, positions, payloadSets, processing, grepMatch, grepExtract, throttle, saveToDb]);

  const loadAttacks = async () => {
    try {
      const data = await intruderService.getAll({ webTargetId: webTarget._id });
      setAttacks(data);
      return data;
    } catch { return []; }
  };

  const buildPayloadSetsForApi = () => {
    return payloadSets.map((ps, i) => {
      const set = {
        insertionPointId: positions[i]?.id || `ip${i}`,
        type: ps.type,
        processing: processing,
      };

      if (ps.type === 'simple_list') {
        set.values = (ps.values || []).filter(Boolean);
      } else if (ps.type === 'numbers') {
        set.numberConfig = ps.numberConfig;
        const cfg = ps.numberConfig || {};
        const values = [];
        for (let n = (cfg.from || 0); n <= (cfg.to || 100); n += (cfg.step || 1)) {
          if (cfg.format === 'hex') {
            values.push(n.toString(16).padStart(cfg.minDigits || 1, '0'));
          } else {
            values.push(n.toString().padStart(cfg.minDigits || 1, '0'));
          }
        }
        set.values = values;
      } else if (ps.type === 'null_payloads') {
        set.values = Array(ps.nullCount || 100).fill('');
      }

      return set;
    });
  };

  const buildPayload = () => ({
    project: webTarget.project,
    webTarget: webTarget._id,
    name: attackName || `Attack ${attacks.length + 1}`,
    attackMode,
    authProfile: authProfile || null,
    baseRequest: { method, url, headers, body },
    insertionPoints: positions,
    payloadSets: buildPayloadSetsForApi(),
    grepMatch,
    grepExtract,
    throttle,
  });

  const selectAttack = (attack) => {
    // Load full details into form
    setEditingAttackId(attack._id);
    setSelectedAttackId(attack._id);
    setAttackName(attack.name || '');
    setAttackMode(attack.attackMode || 'sniper');
    setAuthProfile(attack.authProfile || '');
    setMethod(attack.baseRequest?.method || 'GET');
    setUrl(attack.baseRequest?.url || '');
    setHeaders(attack.baseRequest?.headers || [{ name: 'User-Agent', value: 'Zerberos/1.0' }]);
    setBody(attack.baseRequest?.body || '');
    setPositions(attack.insertionPoints || []);
    setPayloadSets(attack.payloadSets?.map(ps => ({
      type: ps.type || 'simple_list',
      values: ps.values || [],
      numberConfig: ps.numberConfig || { from: 0, to: 100, step: 1, format: 'decimal', minDigits: 1 },
      nullCount: ps.nullCount || 100,
      processing: ps.processing || [],
    })) || [{ type: 'simple_list', values: [], numberConfig: { from: 0, to: 100, step: 1, format: 'decimal', minDigits: 1 }, nullCount: 100, processing: [] }]);
    setProcessing(attack.payloadSets?.[0]?.processing || []);
    setGrepMatch(attack.grepMatch || []);
    setGrepExtract(attack.grepExtract || []);
    setThrottle(attack.throttle || { requestsPerSecond: 10, maxConcurrent: 1, delayBetweenRequests: 100 });
    setResultDetail(null);

    if (attack.status === 'running' || attack.status === 'completed' || attack.status === 'failed') {
      setInnerTab('results');
    } else {
      setInnerTab('positions');
    }
  };

  const handleSelectAttack = async (attack) => {
    // Flush any pending save for current attack before switching
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    if (editingAttackId) {
      try { await intruderService.update(editingAttackId, buildPayload()); } catch {}
    }

    try {
      const full = await intruderService.getById(attack._id);
      selectAttack(full);
    } catch {}
  };

  const resetForm = () => {
    setAttackName('');
    setAttackMode('sniper');
    setAuthProfile('');
    setMethod('GET');
    setUrl('');
    setHeaders([{ name: 'User-Agent', value: 'Zerberos/1.0' }]);
    setBody('');
    setPositions([]);
    setPayloadSets([{
      type: 'simple_list', values: [], numberConfig: { from: 0, to: 100, step: 1, format: 'decimal', minDigits: 1 }, nullCount: 100, processing: []
    }]);
    setProcessing([]);
    setGrepMatch([]);
    setGrepExtract([]);
    setThrottle({ requestsPerSecond: 10, maxConcurrent: 1, delayBetweenRequests: 100 });
    setEditingAttackId(null);
    setSelectedAttackId(null);
  };

  const handleNewAttack = async () => {
    // Flush pending save
    if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    if (editingAttackId) {
      try { await intruderService.update(editingAttackId, buildPayload()); } catch {}
    }

    try {
      const attack = await intruderService.create({
        project: webTarget.project,
        webTarget: webTarget._id,
        name: `Attack ${attacks.length + 1}`,
        attackMode: 'sniper',
        baseRequest: { method: 'GET', url: '', headers: [{ name: 'User-Agent', value: 'Zerberos/1.0' }], body: '' },
        insertionPoints: [],
        payloadSets: [{ insertionPointId: 'ip1', type: 'simple_list', values: [], processing: [] }],
        throttle: { requestsPerSecond: 10, maxConcurrent: 1, delayBetweenRequests: 100 },
      });
      await loadAttacks();
      selectAttack(attack);
      setInnerTab('positions');
    } catch (err) {
      console.error('Error creating attack:', err);
    }
  };

  const handleSaveNow = async () => {
    if (!editingAttackId) {
      // Create new
      try {
        setCreating(true);
        const attack = await intruderService.create(buildPayload());
        setEditingAttackId(attack._id);
        setSelectedAttackId(attack._id);
        await loadAttacks();
      } catch (err) {
        console.error('Error creating attack:', err);
      } finally {
        setCreating(false);
      }
    } else {
      // Force save now
      if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
      try {
        setCreating(true);
        await intruderService.update(editingAttackId, buildPayload());
        await loadAttacks();
      } catch (err) {
        console.error('Error saving attack:', err);
      } finally {
        setCreating(false);
      }
    }
  };

  const handleStart = async (attackId) => {
    // Save first
    if (editingAttackId === attackId) {
      if (saveTimerRef.current) { clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
      try { await intruderService.update(editingAttackId, buildPayload()); } catch {}
    }
    try {
      await intruderService.start(attackId);
      await loadAttacks();
      if (selectedAttackId === attackId) setInnerTab('results');
    } catch (err) {
      console.error('Error starting attack:', err);
    }
  };

  const handlePause = async (attackId) => {
    try { await intruderService.pause(attackId); await loadAttacks(); } catch {}
  };

  const handleCancel = async (attackId) => {
    try { await intruderService.cancel(attackId); await loadAttacks(); } catch {}
  };

  const handleDelete = async (attackId, e) => {
    e?.stopPropagation();
    try {
      await intruderService.remove(attackId);
      if (selectedAttackId === attackId) resetForm();
      await loadAttacks();
    } catch {}
  };

  const handleRequestChange = (parsed) => {
    setMethod(parsed.method);
    setUrl(parsed.url);
    setHeaders(parsed.headers);
    setBody(parsed.body);
  };

  const statusColor = (status) => {
    switch (status) {
      case 'running': return 'text-blue-400';
      case 'completed': return 'text-green-400';
      case 'failed': return 'text-red-400';
      case 'paused': return 'text-yellow-400';
      case 'cancelled': return 'text-gray-400';
      default: return isDarkMode ? 'text-gray-400' : 'text-gray-500';
    }
  };

  const selectedAttack = attacks.find(a => a._id === selectedAttackId);

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className={`flex items-center justify-between px-3 py-2 border-b ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faCrosshairs} className="text-orange-400" />
          <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Intruder
          </span>
          {selectedAttack && (
            <>
              <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>|</span>
              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {selectedAttack.name}
              </span>
              <span className={`text-[10px] ${statusColor(selectedAttack.status)}`}>
                {selectedAttack.status}
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Save button */}
          {editingAttackId && (
            <button
              onClick={handleSaveNow}
              disabled={creating}
              className={`px-3 py-1 rounded text-xs flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              {creating ? <FontAwesomeIcon icon={faSpinner} spin /> : null}
              Save
            </button>
          )}
          {/* Start attack */}
          {selectedAttack?.status === 'configured' && (
            <button
              onClick={() => handleStart(selectedAttack._id)}
              className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 flex items-center gap-1"
            >
              <FontAwesomeIcon icon={faPlay} /> Start Attack
            </button>
          )}
          {selectedAttack?.status === 'running' && (
            <>
              <button
                onClick={() => handlePause(selectedAttack._id)}
                className="px-3 py-1 bg-yellow-600 text-white rounded text-xs hover:bg-yellow-700 flex items-center gap-1"
              >
                <FontAwesomeIcon icon={faPause} /> Pause
              </button>
              <button
                onClick={() => handleCancel(selectedAttack._id)}
                className="px-3 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 flex items-center gap-1"
              >
                <FontAwesomeIcon icon={faStop} /> Cancel
              </button>
            </>
          )}
          {selectedAttack?.status === 'paused' && (
            <button
              onClick={() => handleStart(selectedAttack._id)}
              className="px-3 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 flex items-center gap-1"
            >
              <FontAwesomeIcon icon={faPlay} /> Resume
            </button>
          )}
          <button
            onClick={handleNewAttack}
            className="px-3 py-1 bg-orange-600 text-white rounded text-xs hover:bg-orange-700 flex items-center gap-1"
          >
            <FontAwesomeIcon icon={faPlus} /> New Attack
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Attack list sidebar */}
        <div className={`w-56 border-r overflow-y-auto flex-shrink-0 ${isDarkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50/50'}`}>
          {attacks.map(attack => (
            <div
              key={attack._id}
              onClick={() => handleSelectAttack(attack)}
              className={`px-3 py-2 cursor-pointer border-b group ${
                selectedAttackId === attack._id
                  ? (isDarkMode ? 'bg-orange-900/20 border-gray-700' : 'bg-orange-50 border-gray-200')
                  : (isDarkMode ? 'border-gray-800 hover:bg-gray-800' : 'border-gray-100 hover:bg-gray-50')
              }`}
            >
              <div className="flex items-center justify-between">
                <span className={`text-xs font-medium truncate ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {attack.name}
                </span>
                <button
                  onClick={(e) => handleDelete(attack._id, e)}
                  className={`text-[10px] p-0.5 rounded ${
                    selectedAttackId === attack._id
                      ? (isDarkMode ? 'text-red-400 hover:bg-gray-700' : 'text-red-500 hover:bg-red-50')
                      : 'opacity-0 group-hover:opacity-100 text-red-400 hover:bg-gray-700'
                  }`}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
              <div className={`text-[10px] mt-0.5 flex items-center gap-1.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                <span className={statusColor(attack.status)}>{attack.status}</span>
                <span>{intruderService.getAttackModeConfig(attack.attackMode).label}</span>
              </div>
              {attack.progress?.totalRequests > 0 && (
                <div className="mt-1">
                  <div className={`w-full h-1 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div
                      className="h-1 rounded-full bg-orange-500 transition-all"
                      style={{ width: `${Math.min(100, ((attack.progress.completedRequests || 0) / attack.progress.totalRequests) * 100)}%` }}
                    />
                  </div>
                  <span className={`text-[9px] ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                    {attack.progress.completedRequests}/{attack.progress.totalRequests}
                  </span>
                </div>
              )}
            </div>
          ))}
          {attacks.length === 0 && (
            <div className={`p-4 text-xs text-center ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
              No attacks yet. Click "New Attack" or send a request from Traffic.
            </div>
          )}
        </div>

        {/* Right content area */}
        {editingAttackId ? (
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Inner tab bar */}
            <div className={`flex border-b ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
              {/* Attack name input */}
              <div className="flex items-center px-3 gap-2">
                <input
                  value={attackName}
                  onChange={e => setAttackName(e.target.value)}
                  placeholder="Attack name..."
                  className={`text-xs border-0 bg-transparent py-1.5 w-36 outline-none ${isDarkMode ? 'text-gray-300 placeholder-gray-600' : 'text-gray-700 placeholder-gray-400'}`}
                />
              </div>
              <div className={`w-px ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
              <div className="flex items-center px-3 py-1" title="Attack as this identity (its cookies/auth are injected on every request)">
                <ProfileSelect
                  profiles={authProfiles}
                  value={authProfile}
                  onChange={setAuthProfile}
                  darkMode={isDarkMode}
                />
              </div>
              <div className={`w-px ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
              {INNER_TABS.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setInnerTab(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${
                    innerTab === tab.id
                      ? (isDarkMode ? 'border-orange-400 text-orange-400' : 'border-orange-500 text-orange-600')
                      : (isDarkMode ? 'border-transparent text-gray-500 hover:text-gray-300' : 'border-transparent text-gray-500 hover:text-gray-700')
                  }`}
                >
                  <FontAwesomeIcon icon={tab.icon} className="text-[10px]" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="flex-1 overflow-auto p-4">
              {innerTab === 'positions' && (
                <IntruderPositions
                  attackMode={attackMode}
                  onAttackModeChange={setAttackMode}
                  method={method}
                  url={url}
                  headers={headers}
                  body={body}
                  positions={positions}
                  onPositionsChange={setPositions}
                  onRequestChange={handleRequestChange}
                  isDarkMode={isDarkMode}
                />
              )}

              {innerTab === 'payloads' && (
                <div className="space-y-4">
                  <IntruderPayloads
                    attackMode={attackMode}
                    positions={positions}
                    payloadSets={payloadSets}
                    onPayloadSetsChange={setPayloadSets}
                    isDarkMode={isDarkMode}
                  />
                  <div className={`border-t pt-4 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <IntruderProcessing
                      processing={processing}
                      onProcessingChange={setProcessing}
                      isDarkMode={isDarkMode}
                    />
                  </div>
                </div>
              )}

              {innerTab === 'options' && (
                <IntruderOptions
                  grepMatch={grepMatch}
                  onGrepMatchChange={setGrepMatch}
                  grepExtract={grepExtract}
                  onGrepExtractChange={setGrepExtract}
                  throttle={throttle}
                  onThrottleChange={setThrottle}
                  isDarkMode={isDarkMode}
                />
              )}

              {innerTab === 'results' && (
                <div className="h-full -m-4">
                  <IntruderResults
                    attack={selectedAttack || { _id: editingAttackId, status: 'configured' }}
                    grepMatch={grepMatch}
                    grepExtract={grepExtract}
                    onViewResult={setResultDetail}
                    isDarkMode={isDarkMode}
                  />
                </div>
              )}
            </div>

            {/* Result detail panel */}
            {resultDetail && (
              <div className={`border-t flex-shrink-0 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`} style={{ height: '35%' }}>
                <div className={`flex items-center justify-between px-3 py-1.5 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                  <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Response Detail — Payload: <code className="text-orange-400">{resultDetail.mutation?.mutatedValue}</code>
                  </span>
                  <button
                    onClick={() => setResultDetail(null)}
                    className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}
                  >
                    Close
                  </button>
                </div>
                <div className="flex h-[calc(100%-32px)] overflow-hidden">
                  <div className={`flex-1 overflow-auto p-2 font-mono text-xs whitespace-pre-wrap ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    <div className={`text-[10px] font-semibold mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Request</div>
                    <div>
                      {resultDetail.request?.method} {resultDetail.request?.url || resultDetail.mutation?.mutatedUrl}
                      {'\n'}
                      {(resultDetail.request?.headers || []).map(h => `${h.name}: ${h.value}`).join('\n')}
                      {resultDetail.request?.body ? `\n\n${resultDetail.request.body}` : ''}
                    </div>
                  </div>
                  <div className={`w-px ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                  <div className={`flex-1 overflow-auto p-2 font-mono text-xs whitespace-pre-wrap ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    <div className={`text-[10px] font-semibold mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Response</div>
                    <div>
                      <span className={resultDetail.response?.statusCode < 300 ? 'text-green-500' : resultDetail.response?.statusCode < 500 ? 'text-yellow-500' : 'text-red-500'}>
                        {resultDetail.response?.statusCode}
                      </span>
                      {' '}{resultDetail.response?.statusMessage}
                      {'\n'}
                      {(resultDetail.response?.headers || []).map(h => `${h.name}: ${h.value}`).join('\n')}
                      {resultDetail.response?.body ? `\n\n${resultDetail.response.body?.slice(0, 5000)}` : ''}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className={`flex-1 flex items-center justify-center text-sm ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
            Select an attack from the sidebar or click "New Attack".
          </div>
        )}
      </div>
    </div>
  );
};

export default IntruderTab;
